import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
    Grid, Box, Paper, Typography, Chip, Tooltip, CircularProgress
} from "@mui/material"
import * as actions from "../Common/actions"
import { Link, useNavigate } from "react-router-dom"
import { toastError, toastSuccess, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession, copyToClipboard } from '../../utils'

import { Users, User, Mail, Phone, MapPin, DollarSign, Download, Bell, Copy, ClipboardCheck, Send, X, RefreshCw } from 'lucide-react';
import CustomShowModal from '../Common/customShowModal';
import UserDetailsPasswordModal from './UserDetailsPasswordModal'
import SearchInput from '../../components/SearchInput'
import ActionButton from '../../components/ActionButton'
import EmptyState from '../../components/EmptyState'

const CACHE_KEY = 'userDetailsCache'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function getCachedData() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY)
        if (!raw) return null
        const cached = JSON.parse(raw)
        if (Date.now() - cached.timestamp > CACHE_TTL) {
            sessionStorage.removeItem(CACHE_KEY)
            return null
        }
        return cached
    } catch { return null }
}

function setCachedData(body, active_users) {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
            body,
            active_users,
            timestamp: Date.now()
        }))
    } catch { }
}

function CopyMeta({ icon, label, value }) {
    const [copied, setCopied] = useState(false);
    if (!value || value === '-') {
        return (
            <Tooltip title={label} arrow placement="top">
                <span className="approved-release-card__meta-item">
                    {icon} -
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
            <span
                className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
                onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            >
                {icon} {value}
                {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
            </span>
        </Tooltip>
    );
}

export default function UserDetails(props) {

    const cached = getCachedData()
    const [userDetails, setUserDetails] = useState(cached?.body || [])
    const [userCount, setUserCount] = useState(cached?.active_users || 0)
    const [bgLoading, setBgLoading] = useState(false)
    const navigate = useNavigate();
    const [search, setSearch] = useState('')
    const [selectedUsers, setSelectedUsers] = useState([])
    const [openNotificaton, setOpenNotificaton] = useState(false)
    const [notificationText, setNotificationText] = useState('')
    const [showPasswordModal, setShowPasswordModal] = useState(false)
    const [passwordLoading, setPasswordLoading] = useState(false)
    const [passwordError, setPasswordError] = useState('')
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const refreshTimerRef = useRef(null)

    useEffect(() => {
        document.title = 'UD | User Details';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        const sessionValid = checkUserDetailsSession()
        if (sessionValid) {
            setIsAuthenticated(true)
            onGetNewReleases()
        } else {
            setShowPasswordModal(true)
            clearUserDetailsSession()
        }
    }, [])

    useEffect(() => {
        if (!isAuthenticated) return
        const intervalId = setInterval(() => {
            const sessionValid = checkUserDetailsSession()
            if (!sessionValid) {
                setIsAuthenticated(false)
                setShowPasswordModal(true)
                setPasswordError('Your session has expired. Please re-enter the password.')
            }
        }, 300000)
        return () => clearInterval(intervalId)
    }, [isAuthenticated])

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isAuthenticated) {
                const sessionValid = checkUserDetailsSession()
                if (!sessionValid) {
                    setIsAuthenticated(false)
                    setShowPasswordModal(true)
                    setPasswordError('Your session has expired. Please re-enter the password.')
                }
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [isAuthenticated])

    const onGetNewReleases = (filters = {}, showLoader = false) => {
        const hasCached = getCachedData()
        // If we have cache, fetch silently in background; otherwise show loader
        if (hasCached && !showLoader) {
            setBgLoading(true)
        } else {
            props.setLoader(true)
        }
        actions.getUserListAdmin(filters).then(res => {
            props.setLoader(false)
            setBgLoading(false)
            setUserDetails(res.body)
            setUserCount(res.active_users)
            if (!filters.search) {
                setCachedData(res.body, res.active_users)
            }
        }).catch(err => {
            props.setLoader(false)
            setBgLoading(false)
            toastError(err?.message || 'Failed to load user list');
        })
    }

    // Auto-refresh cache every 5 minutes
    useEffect(() => {
        if (!isAuthenticated) return
        refreshTimerRef.current = setInterval(() => {
            onGetNewReleases({}, false)
        }, CACHE_TTL)
        return () => clearInterval(refreshTimerRef.current)
    }, [isAuthenticated])

    const searchReleases = (value) => {
        setSearch(value)
    }

    const revokeUser = (e, user_id, revoked) => {
        e.stopPropagation()
        props.setLoader(true)
        actions.revokeUnrevokeUserForAdmin({ user_id, revoked }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message);
            onGetNewReleases({}, true)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to revoke/unrevoke user');
        })
    }

    const userDetailsList = useMemo(() => {
        const verified = userDetails.length ? userDetails.filter(item => item.verified === '1') : []
        if (search.trim().length < 3) return verified
        const q = search.toLowerCase()
        return verified.filter(item =>
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.email && item.email.toLowerCase().includes(q)) ||
            (item.phone && item.phone.toLowerCase().includes(q))
        )
    }, [userDetails, search])

    const onClickAddUser = useCallback((item) => {
        let newSelectedUser = [...selectedUsers]
        const findItem = newSelectedUser.findIndex(user => user.user_id === item.user_id)
        if (findItem > -1) {
            newSelectedUser.splice(findItem, 1)
        } else {
            newSelectedUser.push({ user_id: item.user_id, name: item.name, email: item.email })
        }
        setSelectedUsers(newSelectedUser)
    }, [selectedUsers])

    const onSubmitNotification = () => {
        if (notificationText) {
            props.setLoader(true)
            const sendData = {
                user_id: selectedUsers.map(item => item.user_id),
                notification: notificationText
            }
            actions.sendSpecificNotificationsAdmin(sendData).then(res => {
                setSelectedUsers([])
                setOpenNotificaton(false)
                setNotificationText('')
                props.setLoader(false)
                toastSuccess(res.message || 'Success');
            }).catch(err => {
                props.setLoader(false)
                toastError(err?.message || 'Operation failed');
            })
        }
    }

    const handlePasswordVerification = (password) => {
        setPasswordLoading(true)
        setPasswordError('')
        actions.verifyUserDetailsPassword({ password })
            .then((res) => {
                setPasswordLoading(false)
                setShowPasswordModal(false)
                setIsAuthenticated(true)
                setUserDetailsSession(Date.now())
                onGetNewReleases()
                toastSuccess('Access granted')
            })
            .catch((err) => {
                setPasswordLoading(false)
                setPasswordError(err?.message || 'Invalid password. Please try again.')
            })
    }

    const renderUserCard = (item, index) => {
        const isChecked = selectedUsers.find(user => user.user_id === item.user_id) || false
        return (
            <Link
                key={item.user_id}
                to={`/view_user_details/${item.user_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
            >
            <Paper
                className="approved-release-card"
            >
                <div className="approved-release-card__artwork">
                    <div className="approved-release-card__artwork-fallback">
                        <User size={20} color="#64748B" />
                        <span>User</span>
                    </div>
                </div>

                <div className="approved-release-card__body">
                    <div className="approved-release-card__top">
                        <div className="approved-release-card__title-row">
                            <Typography className="approved-release-card__title">
                                {item.name || 'Unnamed User'}
                            </Typography>
                        </div>
                    </div>

                    <div className="approved-release-card__meta">
                        <span className="approved-release-card__id">FM-U-{item.user_id}</span>
                        <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
                        <CopyMeta icon={<Phone size={13} />} label="Phone" value={item.phone} />
                    </div>
                    <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
                        <CopyMeta icon={<MapPin size={13} />} label="Address" value={item.address} />
                        {item.revenue && (
                            <span className="approved-release-card__meta-item" style={{ color: '#10B981', fontWeight: 500 }}>
                                <DollarSign size={13} /> {item.revenue}
                            </span>
                        )}
                    </div>
                </div>

                <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }} onClick={e => e.preventDefault()}>
                    <ActionButton
                        variant={isChecked ? 'settle' : 'info'}
                        sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', gap: 0.5 }}
                        onClick={(e) => { e.preventDefault(); onClickAddUser(item); }}
                    >
                        <Bell size={13} /> {isChecked ? 'Selected' : 'Notify'}
                    </ActionButton>
                    <ActionButton
                        variant="info"
                        sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', gap: 0.5 }}
                        component="a"
                        href={`https://backend.fronicmedia.com/api/generateAgreementForAdmin?user_id=${item.user_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                    >
                        <Download size={13} /> Agreement
                    </ActionButton>
                    {item.revoked === 0 ? (
                        <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={e => { e.preventDefault(); revokeUser(e, item.user_id, 1); }}>
                            Revoke
                        </ActionButton>
                    ) : (
                        <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={e => { e.preventDefault(); revokeUser(e, item.user_id, 0); }}>
                            Unrevoke
                        </ActionButton>
                    )}
                </div>

                <div className="approved-release-card__arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </div>
            </Paper>
            </Link>
        )
    }

    if (!isAuthenticated) {
        return (
            <UserDetailsPasswordModal
                open={showPasswordModal}
                onPasswordVerified={handlePasswordVerification}
                loading={passwordLoading}
                error={passwordError}
            />
        )
    }

    return (
        <>
            <UserDetailsPasswordModal
                open={showPasswordModal}
                onPasswordVerified={handlePasswordVerification}
                loading={passwordLoading}
                error={passwordError}
            />
            <Grid container>
                {/* ========== Header + Search ========== */}
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
                        <Box sx={{ maxWidth: 420, flex: 1 }}>
                            <SearchInput
                                value={search}
                                onChange={(e) => searchReleases(e.target.value)}
                                placeholder="Search by name, email, or phone..."
                            />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
                            {bgLoading && (
                                <Box display="flex" alignItems="center" gap={0.75} sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                    <RefreshCw size={13} className="spin-icon" /> Refreshing...
                                </Box>
                            )}
                            {selectedUsers.length > 0 && (
                                <ActionButton variant="settle" sx={{ gap: 0.5, py: 0.4, px: 2, fontSize: '0.8rem' }} onClick={() => setOpenNotificaton(true)}>
                                    <Bell size={14} /> Notify ({selectedUsers.length})
                                </ActionButton>
                            )}
                            <Chip
                                icon={<Users size={14} />}
                                label={`${userCount} Active`}
                                sx={{
                                    backgroundColor: 'rgba(108, 99, 255, 0.12)',
                                    color: '#6C63FF',
                                    border: '1px solid rgba(108, 99, 255, 0.25)',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    height: 34,
                                    flexShrink: 0,
                                    '& .MuiChip-icon': { color: '#6C63FF' },
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>

                {/* ========== User Cards ========== */}
                <Grid item xs={12}>
                    <Box className="approved-release-list">
                        {userDetailsList.length ? (
                            userDetailsList.map((item, index) => renderUserCard(item, index))
                        ) : (
                            <Paper sx={{ p: 0 }}>
                                <table style={{ width: '100%' }}><tbody>
                                    <EmptyState message="No Users Found" colSpan={1} />
                                </tbody></table>
                            </Paper>
                        )}
                    </Box>
                </Grid>

                {/* ========== Notification Modal ========== */}
                <CustomShowModal
                    open={openNotificaton}
                    title="Send Notification"
                    maxWidth="sm"
                    handleClose={() => setOpenNotificaton(false)}
                >
                    {/* Recipients */}
                    <Box mb={2}>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Recipients ({selectedUsers.length})
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            maxHeight: 120,
                            overflow: 'auto',
                            p: 1.5,
                            backgroundColor: 'var(--bg-elevated)',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                        }}>
                            {selectedUsers.map((item, index) => (
                                <Chip
                                    key={index}
                                    label={item.name}
                                    size="small"
                                    onDelete={() => onClickAddUser(item)}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: 'rgba(108, 99, 255, 0.1)',
                                        color: '#C4B5FD',
                                        border: '1px solid rgba(108, 99, 255, 0.2)',
                                        fontSize: '0.78rem',
                                        '& .MuiChip-deleteIcon': { color: '#94A3B8', '&:hover': { color: '#EF4444' } },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Message */}
                    <Box mb={2}>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Message
                        </Typography>
                        <textarea
                            className="text-area-input"
                            value={notificationText}
                            onChange={e => setNotificationText(e.target.value)}
                            placeholder='Type your notification message here...'
                        />
                    </Box>

                    {/* Actions */}
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <ActionButton variant="info" sx={{ gap: 0.5, px: 2 }} onClick={() => setOpenNotificaton(false)}>
                            Cancel
                        </ActionButton>
                        <ActionButton
                            variant="success"
                            sx={{ gap: 0.5, px: 2.5 }}
                            onClick={onSubmitNotification}
                            disabled={!notificationText.trim()}
                        >
                            <Send size={14} /> Send
                        </ActionButton>
                    </Box>
                </CustomShowModal>
            </Grid>
        </>
    )
}
